<template>
    <div class="fulfillment-info-header">
        <div class="summary-row">
            <div class="order-info">
                <div class="number" @click="onOrderNumberClick">
                    <span>{{ orderNumber }}</span>
                    <i v-if="fulfillment.isLinked" class="fa-light fa-link linked-fulfillment-icon" />
                </div>
                <div class="table">{{ fulfillment.customTableName }}</div>
            </div>
            <div class="order-time-info">
                <div class="time">{{ orderTime }}</div>
                <div class="date">{{ orderDate }}</div>
                <div
                    v-if="fulfilmentReadyDateTime"
                    @click="onFulfilmentReadyDateTimeClick"
                    class="fulfilment-ready-date"
                    :class="{ clickable: fulfillment.orderHasDeliveryAddress }"
                >
                    <i class="fa-light fa-person-carry-box"></i>{{ fulfilmentReadyDateTime }}
                </div>
            </div>
        </div>
        <div class="waiter-and-statuses-row">
            <div class="waiter">{{ waiterName }}</div>
            <div class="statuses-row">
                <div class="use-case">
                    <span v-if="useCaseName">
                        {{ useCaseName }}
                    </span>
                </div>
                <div class="order-states">
                    <OrderStateBadge :orderState="orderState" />
                    <CancellationStateBadge
                        v-if="fulfillment.cancellationState"
                        :cancellationState="fulfillment.cancellationState"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { OrderState } from "@/data/model/Enums";
    import type { IOperatorFulfillment } from "@/data/model/Operator";
    import { DateTime } from "@/data/util/DateTime";
    import { Format } from "@/data/util/Format";
    import { OrderHelper } from "@/data/util/OrderHelper";
    import { OperatorClient } from "@/operator/OperatorClient";
    import { operatorEvents } from "@/operator/events/operatorEvents";
    import { computed } from "vue";

    import CancellationStateBadge from "@/operator/components/order/CancellationStateBadge.vue";
    import OrderStateBadge from "@/operator/components/order/OrderStateBadge.vue";

    const props = defineProps<{
        fulfillment: IOperatorFulfillment;
    }>();

    const fulfilmentReadyDateTime = computed(() => {
        if (props.fulfillment.effectiveFulfillmentDateTime !== undefined) {
            return DateTime.toPreciseDateTimeString(props.fulfillment.effectiveFulfillmentDateTime);
        } else if (props.fulfillment.effectiveReadyDateTime !== undefined) {
            return DateTime.toPreciseDateTimeString(props.fulfillment.effectiveReadyDateTime);
        }

        return DateTime.toPreciseDateTimeString(props.fulfillment.scheduledFulfillmentDateTime);
    });

    const orderNumber = computed(() => {
        return props.fulfillment.dailyOrderIncrement !== undefined
            ? OrderHelper.padDailyIncrement(props.fulfillment.dailyOrderIncrement)
            : props.fulfillment.orderNumber;
    });

    const waiterName = computed(() => {
        return props.fulfillment.orderedByWaiterName;
    });

    const useCaseName = computed(() =>
        Format.translate(props.fulfillment.useCaseName, OperatorClient.context.settings!.location!.language)
    );
    const orderTime = computed(() => DateTime.toTimeString(props.fulfillment.orderDate, true));
    const orderDate = computed(() => DateTime.toDateString(props.fulfillment.orderDate));
    const orderState = computed(() => (props.fulfillment.isPaid ? OrderState.FullyPaid : OrderState.NotPaid));

    function onOrderNumberClick() {
        operatorEvents.fulfillmentDetailsPopup.orderNumberSelected.dispatch(props.fulfillment.orderId);
    }

    function onFulfilmentReadyDateTimeClick() {
        if (props.fulfillment.orderHasDeliveryAddress) {
            operatorEvents.fulfillmentDetailsPopup.showDeliveryAddressClicked.dispatch(props.fulfillment.orderId);
        }
    }
</script>

<style lang="scss">
    @use "./../../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../../assets/styles/scss/shared/_mixins.scss";
    @use "./../../../../assets/styles/scss/shared/_variables.scss";

    .fulfillment-info-header {
        > .summary-row {
            display: flex;
            justify-content: space-between;

            > .order-info {
                > .number {
                    font-size: 18px;
                    line-height: 1em;
                    font-weight: 600;
                    text-transform: uppercase;
                    display: inline-block;
                    padding: 4px 0;
                    position: relative;

                    cursor: pointer;
                    color: colors.$green;
                    transition: color 0.5s variables.$easeOutExpo;

                    @include mixins.hover-and-active() {
                        color: colors.$greenHover;
                    }

                    &:after {
                        content: "";
                        position: absolute;
                        top: 0;
                        right: -4px;
                        bottom: 0;
                        left: -4px;
                    }

                    .linked-fulfillment-icon {
                        margin-left: 0.4em;
                        font-size: 0.9em;
                    }

                    .has-related-fulfillments-icon {
                        margin-left: 0.4em;
                        font-size: 0.9em;
                    }
                }

                > .table {
                    font-size: 16px;
                    line-height: 16px;
                    color: #aaaaaa;
                    font-weight: 600;
                }
            }

            > .order-time-info {
                text-align: right;

                > .time {
                    font-size: 16px;
                    line-height: 20px;
                    font-weight: 600;
                    padding-bottom: 2px;
                }

                > .date {
                    font-size: 14px;
                    line-height: 16px;
                    font-weight: 300;
                    color: #aaaaaa;
                }

                > .fulfilment-ready-date {
                    &.clickable {
                        cursor: pointer;
                        transition: color 0.5s variables.$easeOutExpo;

                        @include mixins.hover-and-active() {
                            color: colors.$fontColorHover;
                        }
                    }
                    padding-top: 4px;
                    font-size: 14px;
                    line-height: 16px;
                    font-weight: 600;
                    color: #aaaaaa;

                    > .fa-person-carry-box {
                        margin-right: 4px;
                        background-color: colors.$green;
                        color: white;
                        padding: 4px;
                        border-radius: 2px;
                    }
                }
            }
        }

        > .waiter-and-statuses-row {
            margin-top: 9px;
            display: flex;
            justify-content: space-between;

            > .waiter {
                font-weight: bold;
            }

            > .statuses-row {
                display: flex;

                > .use-case {
                    span {
                        height: 22px;
                        display: inline-block;
                        line-height: 22px;
                        background-color: #484848;
                        color: #fff;
                        font-size: 12px;
                        font-weight: 400;
                        padding: 0 7px;
                        border-radius: 2px;
                    }
                }

                > .order-states {
                    span {
                        height: 22px;
                        line-height: 22px;
                        color: #fff;
                        font-size: 12px;
                        font-weight: 400;
                        white-space: nowrap;
                    }

                    .order-state-badge,
                    .cancellation-state-badge {
                        padding: 0 7px;
                        border-radius: 2px;
                        margin-left: 3px;
                        display: inline-block;
                    }
                }
            }
        }
    }
</style>
